import ButtonLoader from "components/ButtonLoader";
import { useState } from "react";
import { ErrorToast } from "utils/toast";

const ShareTongueImage = ({ onContinue }) => {
  const [tongueImageData, setTongueImageData] = useState({
    selectedFile: null,
    previewUrl: null,
    isDragging: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [privacyPolicyAgreed, setPrivacyPolicyAgreed] = useState(false);

  const handleFileSelect = (file) => {
    // Validate file type - only images allowed
    const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!file.type || !validImageTypes.includes(file.type.toLowerCase())) {
      ErrorToast("Only JPEG and PNG image files are allowed.");
      return;
    }

    // Optional: Validate file size (e.g., max 10MB)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      ErrorToast("Image size must be less than 10MB.");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setTongueImageData({ selectedFile: file, previewUrl, isDragging: false });
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setTongueImageData((prev) => ({ ...prev, isDragging: true }));
  };

  const handleDragLeave = () => {
    setTongueImageData((prev) => ({ ...prev, isDragging: false }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleContinue = async () => {
    if (tongueImageData.selectedFile) {
      try {
        setIsLoading(true);
        await onContinue(tongueImageData.selectedFile);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <h2 className="question-title" style={{ textAlign: "center" }}>
        Share Your Tongue Image
      </h2>
      <p className="step-description">
        In Chinese medicine, the tongue reflects the health of your internal
        organs. Its color, shape, and coating help reveal imbalances in the
        body.
      </p>

      <div className="form-section">
        <div
          className={`file-upload-area ${
            tongueImageData.isDragging ? "dragging" : ""
          } ${tongueImageData.selectedFile ? "has-file" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {tongueImageData.selectedFile ? (
            <div className="file-preview">
              <img
                src={tongueImageData.previewUrl}
                alt="Tongue preview"
                className="preview-image"
              />
              <div className="file-info">
                <p className="file-name">{tongueImageData.selectedFile.name}</p>
                <button
                  className="remove-file-btn"
                  onClick={() =>
                    setTongueImageData({
                      selectedFile: null,
                      previewUrl: null,
                      isDragging: false,
                    })
                  }
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="upload-placeholder">
              <h3 className="upload-title">
                Choose a file or drag & drop it here
              </h3>
              <p className="upload-subtitle">JPEG and PNG formats only.</p>
            </div>
          )}
        </div>

        <div className="upload-buttons">
          <input
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
            id="file-input"
          />
          <label htmlFor="file-input" className="upload-btn browse-btn">
            Browse File
          </label>
          <button
            className="upload-btn photo-btn"
            onClick={() => {
              const cameraInput = document.createElement("input");
              cameraInput.type = "file";
              cameraInput.accept = "image/*";
              cameraInput.capture = "environment"; // rear camera
              cameraInput.style.display = "none";

              cameraInput.onchange = (e) => {
                const file = e.target.files[0];
                if (file) handleFileSelect(file);
                document.body.removeChild(cameraInput);
              };

              document.body.appendChild(cameraInput);
              cameraInput.click();
            }}
          >
            Take a Photo
          </button>
        </div>
      </div>
      {/* Privacy Policy Agreement */}
      <div className="privacy-consent-box">
        <label className="privacy-consent-label">
          <input
            type="checkbox"
            name="privacyPolicyAgreed"
            checked={privacyPolicyAgreed}
            onChange={(e) => setPrivacyPolicyAgreed(e.target.checked)}
            className="privacy-consent-checkbox"
          />
          <span>
            By submitting, you agree to our{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="privacy-consent-link"
            >
              Privacy Policy
            </a>
          </span>
        </label>
      </div>

      <button
        className={`continue-btn ${
          tongueImageData.selectedFile && privacyPolicyAgreed ? "active" : ""
        }`}
        onClick={handleContinue}
        disabled={
          !tongueImageData.selectedFile || !privacyPolicyAgreed || isLoading
        }
      >
        {isLoading ? <ButtonLoader /> : "Complete"}
      </button>
    </>
  );
};

export default ShareTongueImage;
